// src/context/AgendaAuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AgendaAuthContext = createContext();

export function useAgendaAuth() {
  const context = useContext(AgendaAuthContext);
  if (!context) {
    throw new Error('useAgendaAuth must be used within AgendaAuthProvider');
  }
  return context;
}

export function AgendaAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Carregar token do localStorage ao iniciar
  useEffect(() => {
    const storedToken = localStorage.getItem('agenda_token');
    if (storedToken) {
      setToken(storedToken);
      fetchUserInfo(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // Buscar informacoes do usuario logado
  const fetchUserInfo = async (authToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agenda/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        // Token invalido, limpar
        localStorage.removeItem('agenda_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      localStorage.removeItem('agenda_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Registrar novo usuario
  const register = async (email, password, name, whatsappNumber, inviteToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agenda/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name, whatsappNumber, inviteToken })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('agenda_token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agenda/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('agenda_token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (token) {
        await fetch(`${process.env.REACT_APP_API_URL}/api/agenda/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('agenda_token');
      setToken(null);
      setUser(null);
    }
  };

  // Atualizar perfil
  const updateProfile = async (name, whatsappNumber) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agenda/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, whatsappNumber })
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        return { success: true, data };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    token,
    loading,
    register,
    login,
    logout,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AgendaAuthContext.Provider value={value}>
      {children}
    </AgendaAuthContext.Provider>
  );
}
